import type { ReactNode } from "react";
import { ApiError } from "../lib/api";
import { Icon, type IconName } from "./Icon";

/** A row of shimmering bars, sized to the block it stands in for. */
export function Skeleton({ lines = 3, width = "100%" }: { lines?: number; width?: string }) {
  return (
    <div className="stack-tight" aria-hidden="true" style={{ width }}>
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="skeleton"
          style={{ width: i === lines - 1 ? "62%" : "100%", height: i === 0 ? "1.25rem" : "0.75rem" }}
        />
      ))}
    </div>
  );
}

export function LoadingBlock({ label = "Loading" }: { label?: string }) {
  return (
    <div className="stack" aria-busy="true" aria-live="polite">
      <p className="row dim" style={{ gap: "0.5rem" }}>
        <span className="spinner" />
        <span>{label}…</span>
      </p>
      <Skeleton lines={4} />
    </div>
  );
}

export function EmptyState({
  icon = "book",
  title,
  children,
  action,
}: {
  icon?: IconName;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <p className="dim" aria-hidden="true">
        <Icon name={icon} size={20} />
      </p>
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

/** The standard way to show a failed load in place, with a way back. */
export function ErrorState({
  error,
  onRetry,
  title = "This did not load",
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const message = error instanceof ApiError
    ? error.message
    : error instanceof Error && error.message
      ? error.message
      : "Something went wrong on the way to the server.";
  return (
    <div className="empty" role="alert">
      <p style={{ color: "var(--rust)" }} aria-hidden="true">
        <Icon name="warning" size={20} />
      </p>
      <h3>{title}</h3>
      <p>{message}</p>
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          <Icon name="refresh" />
          Try again
        </button>
      ) : null}
    </div>
  );
}
